import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";

/**
 * ادیتور سبک برای فیلد «توضیحات محصول».
 * خروجی: رشته‌ی HTML (مثلاً <p>...</p><p><strong>...</strong></p>)
 * که همون رشته داخل فیلد description (از نوع text در بک‌اند) ذخیره می‌شه.
 *
 * چون بک‌اند فقط یک فیلد متنی ساده‌ست، نیازی به تغییر بک‌اند نیست؛
 * فقط موقع نمایش توضیحات (مثلاً صفحه‌ی محصول برای خریدار) باید این HTML
 * رو با dangerouslySetInnerHTML رندر کنید (پایین توضیح داده شده).
 */
export default function ApfDescriptionEditor({
  value,
  onChange,
  placeholder = "توضیحی کوتاه درباره محصول بنویسید...",
}) {
  // آخرین HTML‌ای که خودِ ادیتور تولید و به بیرون فرستاده (از onUpdate)
  // برای اینکه useEffectِ sync، همون تایپِ لحظه‌ای رو دوباره روی خودش ننویسه
  const lastEmittedRef = useRef(value || "");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // فقط چیزهایی که لازم داریم فعال باشن؛ بقیه رو می‌تونید بعداً اضافه کنید
        heading: false,
        codeBlock: false,
        blockquote: false,
      }),
    ],
    content: value || "",
    onCreate: ({ editor }) => {
      // اطمینان از اینکه سند تازه‌ساز هیچ فرمت باقی‌مونده‌ای (بولد/ایتالیک) نداره
      if (editor.isEmpty) editor.commands.unsetAllMarks();
    },
    onUpdate: ({ editor }) => {
      // اگر ادیتور کاملاً خالیه، رشته‌ی خالی بفرست (نه "<p></p>")
      const html = editor.isEmpty ? "" : editor.getHTML();
      lastEmittedRef.current = html;
      onChange(html);
    },
    onSelectionUpdate: ({ editor }) => {
      // باگ اصلی: وقتی کل متن پاک می‌شه (یا از اول چیزی نوشته نشده)، ProseMirror
      // مارک‌های فعالِ قبلی (مثل بولد) رو به‌عنوان "stored marks" نگه می‌داره و
      // کاراکتر بعدی که تایپ می‌شه با همون فرمتِ قبلی (بولد) درج می‌شه.
      // با خالی‌شدن سند، همه‌ی مارک‌های باقی‌مونده رو پاک می‌کنیم.
      if (editor.isEmpty) {
        editor.commands.unsetAllMarks();
      }
    },
  });

  // اگه مقدار description از بیرون عوض بشه (مثلاً موقع لود محصول در حالت ویرایش)
  // و با محتوای فعلی ادیتور فرق داشته باشه، ادیتور رو sync کن.
  // توجه: اگه این تغییرِ value همون چیزیه که خودِ ادیتور همین الان از onUpdate
  // فرستاده، دوباره setContent صدا نمی‌زنیم؛ چون setContent باعث ریست شدنِ
  // انتخاب/مکان‌نما و بی‌ثباتی حین تایپ می‌شه.
  useEffect(() => {
    if (!editor) return;
    if ((value || "") === lastEmittedRef.current) return;
    const current = editor.isEmpty ? "" : editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
      lastEmittedRef.current = value || "";
      if (editor.isEmpty) editor.commands.unsetAllMarks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const isEmptyDoc = editor.isEmpty;

  return (
    <div className="apf-rte">
      <div className="apf-rte-toolbar">
        <button
          type="button"
          className={`apf-rte-btn ${editor.isActive("bold") ? "apf-rte-btn--active" : ""}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="بولد (Ctrl+B)"
        >
          B
        </button>
        <button
          type="button"
          className={`apf-rte-btn ${editor.isActive("italic") ? "apf-rte-btn--active" : ""}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="ایتالیک (Ctrl+I)"
        >
          I
        </button>
        <button
          type="button"
          className={`apf-rte-btn ${editor.isActive("bulletList") ? "apf-rte-btn--active" : ""}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="لیست"
        >
          •
        </button>
      </div>
      <div className="apf-rte-body-wrap">
        <EditorContent editor={editor} className="apf-rte-body" />
        {isEmptyDoc && (
          <span className="apf-rte-placeholder">{placeholder}</span>
        )}
      </div>
    </div>
  );
}
